import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarDays, MapPin, Users, Euro, Clock, Phone, FileText, Loader2, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { tApi, fileUrl, adminHeaders, STATUS_CLS, CATEGORY_LABEL, fmtDate, fmtDateShort } from "@/lib/tournaments";
import RegistrationDialog from "@/components/tournaments/RegistrationDialog";
import MatchList from "@/components/tournaments/MatchList";
import StandingsTable from "@/components/tournaments/StandingsTable";
import PhotoGallery from "@/components/tournaments/PhotoGallery";
import Markdown from "@/components/tournaments/Markdown";

import { applySeo } from "@/lib/seo";
const TABS = [
  ["info", "ΠΛΗΡΟΦΟΡΙΕΣ"],
  ["rules", "ΚΑΝΟΝΙΣΜΟΙ"],
  ["teams", "ΟΜΑΔΕΣ"],
  ["schedule", "ΠΡΟΓΡΑΜΜΑ & ΑΠΟΤΕΛΕΣΜΑΤΑ"],
  ["standings", "ΒΑΘΜΟΛΟΓΙΑ ΟΜΙΛΩΝ"],
  ["final", "ΤΕΛΙΚΗ ΚΑΤΑΤΑΞΗ"],
  ["photos", "ΦΩΤΟΓΡΑΦΙΕΣ"],
];

export default function TournamentDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [regOpen, setRegOpen] = useState(false);

  const load = useCallback(() => {
    const preview = new URLSearchParams(window.location.search).get("preview") && localStorage.getItem("sandlab_admin_token");
    const req = preview ? tApi.get(`/tournaments/admin/${slug}`, { headers: adminHeaders() }) : tApi.get(`/tournaments/${slug}`);
    return req.then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data) applySeo({ title: data.tournament.title, description: `${data.tournament.title} — beach volleyball tournament in Chania, Greece by The Sand Lab. Teams, groups, matches and standings.`, image: data.tournament.cover_url || data.tournament.cover_image || undefined }); }, [data]);

  if (err) return <Shell><div className="text-center py-32 text-slate-500" data-testid="tournament-not-found">Το τουρνουά δεν βρέθηκε.</div></Shell>;
  if (!data) return <Shell><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-ocean" /></div></Shell>;

  const { tournament: t, teams, groups, matches, standings, placements, photos, waitlist_count } = data;
  const canRegister = t.status === "open" || (t.status === "closed" && t.allow_waitlist && !(t.registration_deadline && t.registration_deadline < new Date().toISOString().slice(0, 10)));
  const full = t.available_spots <= 0;

  return (
    <Shell>
      <section className="relative bg-ocean text-white" data-testid="tournament-banner">
        {t.cover_url && <img src={fileUrl(t.cover_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />}
        <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-12">
          <Link to="/tournaments" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white mb-6" data-testid="tournament-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Όλα τα τουρνουά
          </Link>
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className={`${STATUS_CLS[t.status]} text-[10px] uppercase tracking-widest`} data-testid="tournament-status">{t.status_label}</Badge>
            <Badge className="bg-white/90 text-ocean text-[10px] uppercase tracking-widest">{CATEGORY_LABEL[t.category]}</Badge>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight max-w-4xl" data-testid="tournament-title">{t.title}</h1>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/90">
            <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-sunset" />{fmtDate(t.date)}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-sunset" />{t.start_time || "Ώρα: θα ανακοινωθεί"}</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-sunset" />{t.location || "—"}</span>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Tabs defaultValue="info">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-white border border-slate-100 rounded-2xl p-1 shadow-soft justify-start" data-testid="tournament-tabs">
            {TABS.map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-xl text-[11px] sm:text-xs font-bold tracking-wide data-[state=active]:bg-ocean data-[state=active]:text-white">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="info" className="mt-8">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <Card title="Περιγραφή">
                  <p className="text-slate-700 whitespace-pre-line leading-relaxed" data-testid="tournament-description">{t.notes || t.short_description}</p>
                </Card>
                {t.format && <Card title="Μορφή Διοργάνωσης"><p className="text-slate-700 whitespace-pre-line">{t.format}</p></Card>}
              </div>
              <div className="space-y-6">
                <Card title="Στοιχεία">
                  <dl className="space-y-3 text-sm">
                    <Row icon={Euro} k="Συμμετοχή" v={t.fee === "FREE" || !t.fee ? "Δωρεάν" : t.fee} />
                    <Row icon={Users} k="Μέγιστος αριθμός ομάδων" v={t.max_teams} />
                    <Row icon={Users} k="Διαθέσιμες θέσεις" v={<span data-testid="tournament-spots">{t.available_spots}</span>} />
                    <Row icon={CalendarDays} k="Προθεσμία δηλώσεων" v={t.registration_deadline ? fmtDateShort(t.registration_deadline) : "Θα ανακοινωθεί"} />
                    <Row icon={Phone} k="Επικοινωνία" v={t.contact || <a href="tel:6980835933" className="text-ocean font-semibold">698 083 5933</a>} />
                  </dl>
                </Card>
                <div className="rounded-3xl bg-white border border-slate-100 shadow-soft p-6 text-center">
                  {full && <div className="text-sm font-black tracking-widest text-amber-700 mb-3" data-testid="tournament-full">ΟΙ ΘΕΣΕΙΣ ΕΧΟΥΝ ΣΥΜΠΛΗΡΩΘΕΙ</div>}
                  {canRegister ? (
                    <button onClick={() => setRegOpen(true)} data-testid="tournament-register-btn" className="w-full h-12 rounded-full bg-sunset hover:bg-sunset-hover text-white font-black tracking-widest text-sm transition-colors">
                      ΔΗΛΩΣΗ ΣΥΜΜΕΤΟΧΗΣ{full ? " (ΛΙΣΤΑ ΑΝΑΜΟΝΗΣ)" : ""}
                    </button>
                  ) : (
                    <div className="text-sm text-slate-500" data-testid="tournament-register-closed">
                      {t.status === "upcoming" ? `Οι δηλώσεις ανοίγουν ${t.registration_open ? fmtDateShort(t.registration_open) : "σύντομα"}.` : "Οι δηλώσεις συμμετοχής έχουν κλείσει."}
                    </div>
                  )}
                  {waitlist_count > 0 && <div className="text-xs text-slate-500 mt-2">{waitlist_count} ομάδες σε λίστα αναμονής</div>}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="rules" className="mt-8">
            <Card title="Κανονισμοί Διοργάνωσης">
              <Markdown text={t.regulations} />
              {t.regulations_url && (
                <a href={fileUrl(t.regulations_url)} target="_blank" rel="noreferrer" data-testid="tournament-regulations-pdf" className="inline-flex items-center gap-2 mt-6 rounded-full border border-ocean text-ocean px-5 py-2 text-sm font-bold hover:bg-ocean/5">
                  <FileText className="h-4 w-4" /> Κανονισμοί (PDF)
                </a>
              )}
              {t.forfeit_rule && <p className="mt-6 text-xs text-slate-500">Άνευ αγώνος / αποχώρηση: {t.forfeit_rule}</p>}
            </Card>
          </TabsContent>

          <TabsContent value="teams" className="mt-8">
            <Card title={`Συμμετέχουσες Ομάδες (${teams.length}/${t.max_teams})`}>
              {teams.length === 0 ? <Empty text="Δεν υπάρχουν ακόμη εγκεκριμένες ομάδες." /> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tournament-teams-table">
                    <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b"><th className="py-2 pr-4">Ομάδα</th><th className="py-2 pr-4">Αθλήτρια 1</th><th className="py-2 pr-4">Αθλήτρια 2</th><th className="py-2 pr-4">Όμιλος</th><th className="py-2">Κατάσταση</th></tr></thead>
                    <tbody>
                      {teams.map((tm) => (
                        <tr key={tm.id} className="border-b border-slate-50" data-testid={`team-row-${tm.id}`}>
                          <td className="py-3 pr-4 font-bold text-slate-900">{tm.name}</td>
                          <td className="py-3 pr-4">{tm.player1}</td>
                          <td className="py-3 pr-4">{tm.player2}</td>
                          <td className="py-3 pr-4">{tm.group_name || "—"}</td>
                          <td className="py-3"><Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Εγκρίθηκε</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="schedule" className="mt-8">
            <Card title="Πρόγραμμα & Αποτελέσματα">
              {matches.length === 0 ? <Empty text="Το πρόγραμμα θα ανακοινωθεί." /> : <MatchList matches={matches} />}
            </Card>
          </TabsContent>

          <TabsContent value="standings" className="mt-8 space-y-6">
            {groups.length === 0 ? <Card title="Βαθμολογία Ομίλων"><Empty text="Οι όμιλοι δεν έχουν οριστεί ακόμη." /></Card> :
              groups.map((g) => <Card key={g.id} title={g.name}><StandingsTable rows={standings[g.id] || []} /></Card>)}
          </TabsContent>

          <TabsContent value="final" className="mt-8">
            <Card title="Τελική Κατάταξη">
              {placements.length === 0 ? <Empty text="Η τελική κατάταξη θα ανακοινωθεί μετά την ολοκλήρωση του τουρνουά." /> : (
                <ol className="space-y-2" data-testid="tournament-placements">
                  {placements.map((p) => (
                    <li key={p.id} className={`flex items-center justify-between rounded-2xl px-4 py-3 ${p.pos_from === 1 ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`} data-testid={`placement-${p.team_id}`}>
                      <div className="flex items-center gap-4">
                        <span className={`font-heading font-black text-lg w-16 ${p.pos_from === 1 ? "text-amber-600" : "text-ocean"}`}>{p.placement_label}</span>
                        <span className="font-bold text-slate-900">{p.team_name}</span>
                      </div>
                      <span className="text-sm font-bold text-slate-600">{p.points} πόντοι</span>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="photos" className="mt-8">
            <Card title="Φωτογραφίες">
              {photos.length === 0 ? <Empty text="Δεν υπάρχουν φωτογραφίες ακόμη." /> : <PhotoGallery photos={photos} />}
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <RegistrationDialog open={regOpen} onOpenChange={setRegOpen} tournament={t} full={full} onDone={load} />
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FAF6EF]" data-testid="tournament-detail-page">
    <Navbar />
    {children}
    <Footer />
  </div>
);

export const Card = ({ title, children }) => (
  <section className="rounded-3xl bg-white border border-slate-100 shadow-soft p-6 md:p-8">
    <h2 className="font-heading text-base md:text-lg font-black text-slate-900 mb-5">{title}</h2>
    {children}
  </section>
);

const Row = ({ icon: Icon, k, v }) => (
  <div className="flex items-start gap-3">
    <Icon className="h-4 w-4 text-sunset mt-0.5 shrink-0" />
    <div><dt className="text-[11px] uppercase tracking-widest text-slate-500">{k}</dt><dd className="font-semibold text-slate-900">{v}</dd></div>
  </div>
);

const Empty = ({ text }) => <p className="text-sm text-slate-500 py-6 text-center">{text}</p>;
